/**
 * Represents a node in the hybrid LinkedList.
 * @template T The type of value stored in the node.
 */
export class Node<T = unknown> {
  public constructor(
    /** The value stored in this node. */
    public value: T,
    /** Reference to the next node in the list. */
    public next: Node<T> | null = null,
    /** Reference to the previous node in the list. */
    public prev: Node<T> | null = null,
  ) {}
}

/**
 * A hybrid data structure combining a Doubly Linked List with a Map.
 *
 * This structure allows for:
 * - **O(1) Insertion**: Append to the end of the list.
 * - **O(1) Removal**: Remove any element by its unique key.
 * - **O(1) Random Access**: Retrieve any element by its unique key.
 * - **O(1) Head/Tail Access**: Direct access to the first and last elements.
 * - **Ordered Iteration**: Iteration follows insertion order (like a standard Linked List).
 *
 * ### Why use this over a standard Array?
 * Arrays provide O(1) random access by *index*, but O(n) removal by value/key (shift).
 * This structure provides O(1) removal by *key*.
 *
 * ### Why use this over a standard Map?
 * While JS `Map` preserves insertion order, it does not expose `head` (first) or `tail` (last)
 * elements directly without iteration or maintaining separate state. This structure exposes
 * explicit `head` and `tail` pointers, which is useful for queue/stack-like operations where
 * you also need random access removal (e.g., removing a specific item from the middle of a queue).
 *
 * @example
 * // UI Portal Management (Stack with random removal)
 * const portals = new LinkedList<HTMLElement>();
 *
 * // Add portals (O(1))
 * portals.add('modal-1', modalEl);
 * portals.add('toast-1', toastEl);
 *
 * // Check the top-most portal (O(1))
 * const topPortal = portals.tail?.value;
 *
 * // Remove a specific portal by ID, even if it's not the top one (O(1))
 * portals.remove('modal-1');
 */
export class LinkedList<T = unknown> {
  /** The first node in the list. */
  public head: Node<T> | null = null;
  /** The last node in the list. */
  public tail: Node<T> | null = null;

  /**
   * Internal Map to store nodes by their unique keys for O(1) access.
   * Exposed public for direct size checks or iteration if needed.
   */
  public map = new Map<string, Node<T>>();

  /**
   * Adds a new element to the end of the list.
   *
   * @param key Unique key for the new element.
   * @param value Value of the new element.
   * @throws {Error} If the key already exists in the list.
   * @complexity O(1)
   */
  public add(key: string, value: T): void {
    if (this.map.has(key)) {
      throw new Error(`Key "${key}" already exists in the list.`);
    }

    const newNode = new Node(value);

    if (!this.head) {
      // If the list is empty, the new node becomes both the head and the tail
      this.head = newNode;
      this.tail = newNode;
    } else {
      // Add the new node to the end of the list
      newNode.prev = this.tail;
      if (this.tail) {
        this.tail.next = newNode;
      }
      this.tail = newNode;
    }

    // Store the node in the Map
    this.map.set(key, newNode);
  }

  /**
   * Removes an element from the list by its key.
   *
   * @param key Unique key of the element to remove.
   * @returns The removed node.
   * @throws {Error} If the key is not found in the list.
   * @complexity O(1)
   */
  public remove(key: string): Node<T> {
    const node = this.map.get(key);
    if (!node) {
      throw new Error(`Key "${key}" not found in the list.`);
    }

    // Adjust pointers
    if (node.prev) {
      node.prev.next = node.next;
    } else {
      // If the node to remove is the head, move the head pointer
      this.head = node.next;
    }

    if (node.next) {
      node.next.prev = node.prev;
    } else {
      // If the node to remove is the tail, move the tail pointer
      this.tail = node.prev;
    }

    // Remove the node from the Map
    this.map.delete(key);

    return node;
  }

  /**
   * Gets the value of an element by its key.
   *
   * @param key Unique key of the element.
   * @returns Value of the element or null if the key is not found.
   * @complexity O(1)
   */
  public get(key: string): T | null {
    return this.map.get(key)?.value || null;
  }

  /**
   * Returns all the values of the list as an array, preserving insertion order.
   *
   * @returns Array of values in the list.
   * @complexity O(n)
   */
  public toArray(): T[] {
    const result: T[] = [];
    for (let current = this.head; current !== null; current = current.next) {
      result.push(current.value);
    }
    return result;
  }
}
